import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, description, className, id, checked, disabled, ...rest },
  ref,
) {
  const ariaLabel = label ?? rest['aria-label'];
  return (
    <label
      htmlFor={id}
      className={cn('group inline-flex items-center gap-3 cursor-pointer', disabled && 'cursor-not-allowed opacity-60', className)}
    >
      <span className="relative inline-flex items-center">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          className="peer sr-only"
          aria-label={!label ? ariaLabel : undefined}
          {...rest}
        />
        <span
          className={cn(
            'inline-flex h-7 w-12 items-center rounded-full bg-bg-surface-3 border border-border-strong transition-colors duration-150',
            'peer-checked:bg-accent-base peer-checked:border-accent-base',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent-base',
          )}
          aria-hidden
        >
          <span
            className={cn(
              'mx-1 inline-block size-5 rounded-full bg-white shadow transition-transform duration-150',
              'peer-checked:translate-x-5 group-has-[input:checked]:translate-x-5',
            )}
          />
        </span>
      </span>
      {(label || description) && (
        <span className="flex flex-col">
          {label && <span className="text-base font-medium text-text-primary">{label}</span>}
          {description && <span className="text-sm text-text-secondary">{description}</span>}
        </span>
      )}
    </label>
  );
});
